import React from 'react';
import {
	FormattedMessage,
	type MessageDescriptor,
	type WrappedComponentProps,
	injectIntl,
} from 'react-intl-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { getTrigger, isNewTab } from '../../../common/analytics';
import { AdvancedSearchLinkSubjectId } from '../../../common/analytics/events';
import { messages } from '../../../messages';
import { type JiraFeatures, type AdvancedSearchLinksProps } from './types';
import { useQuery } from '../../query-context';

import {
	AdvancedSearchContainer,
	LabelContainer,
	StyledLink,
} from './single-site-advanced-search.styled';

type SearchConfiguration = {
	href: string;
	message: MessageDescriptor;
	actionSubjectId: AdvancedSearchLinkSubjectId;
};

export const getSearchConfiguration: (
	query: string,
	features?: JiraFeatures,
) => { [key: string]: SearchConfiguration } = (query, features) => {
	let allLinks: { [key: string]: SearchConfiguration } = {
		board: {
			href: `/secure/ManageRapidViews.jspa?contains=${query}`,
			message: messages.jira_advanced_search_boards,
			actionSubjectId: AdvancedSearchLinkSubjectId.JIRA_BOARDS,
		},
		project: {
			href: `/projects?contains=${query}`,
			message: messages.jira_advanced_search_projects,
			actionSubjectId: AdvancedSearchLinkSubjectId.JIRA_PROJECTS,
		},
		filters: {
			href: `/secure/ManageFilters.jspa?Search=Search&filterView=search&name=${query}`,
			message: messages.jira_advanced_search_filters,
			actionSubjectId: AdvancedSearchLinkSubjectId.JIRA_FILTERS,
		},
		plans: {
			href: `/jira/plans`,
			message: messages.jira_advanced_search_plans,
			actionSubjectId: AdvancedSearchLinkSubjectId.JIRA_PLANS,
		},
		people: {
			href: `/jira/people/search?q=${query}`,
			message: messages.jira_advanced_search_people,
			actionSubjectId: AdvancedSearchLinkSubjectId.JIRA_PEOPLE,
		},
	};

	// Remove access to plans if the user does not have Advanced Roadmaps access
	if (!features?.hasAdvancedRoadmapsAccess) {
		delete allLinks.plans;
	}
	// Remove access to boards if the user does not have software access
	if (!features?.hasAdvancedRoadmapsAccess && !features?.hasSoftwareAccess) {
		delete allLinks.board;
	}

	return allLinks;
};

export const SingleSiteAdvancedSearch: React.FC<
	AdvancedSearchLinksProps & WrappedComponentProps
> = ({ advancedSearchSelected, features, linkComponent }) => {
	const { query, isLoading } = useQuery();

	const onClickAndFireEvent = (
		e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
		actionSubjectId: AdvancedSearchLinkSubjectId,
	) => {
		e.preventDefault();
		advancedSearchSelected({
			actionSubjectId,
			trigger: getTrigger(e),
			isLoading,
			newTab: isNewTab(e),
		});
	};

	const searchConfiguration = getSearchConfiguration(query, features);

	return (
		<AdvancedSearchContainer>
			<Box as="ul" xcss={advancedSearchListStyles}>
				<LabelContainer>
					<FormattedMessage {...messages.jira_advanced_search_label} />
				</LabelContainer>
				{Object.keys(searchConfiguration).map((key) => {
					const { href, message, actionSubjectId } = searchConfiguration[key];

					return (
						<Box as="li" xcss={advancedSearchListItemStyles}>
							{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
							<span
								onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
									onClickAndFireEvent(e, actionSubjectId)
								}
								role="none"
							>
								<StyledLink linkComponent={linkComponent} key={`search_link_${key}`} href={href}>
									<FormattedMessage {...message} />
								</StyledLink>
							</span>
						</Box>
					);
				})}
			</Box>
		</AdvancedSearchContainer>
	);
};

const advancedSearchListStyles = xcss({
	padding: '0',
	margin: '0',
	listStyle: 'none',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

const advancedSearchListItemStyles = xcss({
	margin: '0',
	padding: '0',
});

export default injectIntl(SingleSiteAdvancedSearch);
